import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAppSelector } from '../../../shared/store/hooks';
import { useHeroSliderPublic } from '../context/useHeroSlider';
import { HeroSliderPublicProvider } from '../context/HeroSliderContext';
import { heroSliderService } from '../services/heroSliderService';
import { SLIDER_CONFIG } from '../sliderConfig';
import { HeroSliderData } from '../types/heroSlider.types';
import '../styles/HeroSliderPublic.css';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/api/v1', '') ?? 'http://localhost:8000';

/* ── Inner slider (consumes context) ── */
const SliderInner: React.FC = () => {
    const { state, fetchPublic } = useHeroSliderPublic();
    const { isAuthenticated }    = useAppSelector(s => s.auth);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused]         = useState(false);
    const timerRef   = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const touchStartX  = useRef<number>(0);

    const audience = isAuthenticated ? 'LOGGED_IN' : 'GUEST';

    useEffect(() => { fetchPublic(audience); }, [fetchPublic, audience]);

    const { banners, loading } = state;
    const total = banners.length;

    const goTo = useCallback((idx: number) => {
        if (total === 0) return;
        setCurrentIndex(SLIDER_CONFIG.infiniteLoop
            ? (idx + total) % total
            : Math.max(0, Math.min(idx, total - 1))
        );
    }, [total]);

    const goNext = useCallback(() => goTo(currentIndex + 1), [goTo, currentIndex]);
    const goPrev = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex]);

    /* Auto-slide */
    useEffect(() => {
        if (!SLIDER_CONFIG.autoSlide || isPaused || total <= 1) return;
        timerRef.current = setInterval(goNext, SLIDER_CONFIG.slideDuration);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [goNext, isPaused, total]);

    /* Keyboard navigation */
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft')  goPrev();
            if (e.key === 'ArrowRight') goNext();
        };
        el.addEventListener('keydown', handler);
        return () => el.removeEventListener('keydown', handler);
    }, [goPrev, goNext]);

    /* Swipe gestures */
    const handleTouchStart = (e: React.TouchEvent) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd   = (e: React.TouchEvent) => {
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 50) delta > 0 ? goNext() : goPrev();
    };

    const trackClick = (banner: HeroSliderData) => {
        heroSliderService.trackClick(banner._id).catch(() => {});
        if (banner.ctaUrl) window.open(banner.ctaUrl, banner.ctaTarget ?? '_self');
    };

    if (loading) {
        return (
            <div className="hsp-loading">
                <div className="hsp-loading-spinner" aria-label="Loading banners" />
            </div>
        );
    }

    if (total === 0) {
        return <div className="hsp-empty" aria-hidden="true" />;
    }

    const isFade = SLIDER_CONFIG.animationType === 'fade';
    const cssVars = { '--hsp-speed': `${SLIDER_CONFIG.animationSpeed}ms` } as React.CSSProperties;

    return (
        <div
            ref={containerRef}
            className="hsp-root"
            style={cssVars}
            tabIndex={0}
            aria-label="Hero banner slider"
            onMouseEnter={() => SLIDER_CONFIG.pauseOnHover && setIsPaused(true)}
            onMouseLeave={() => SLIDER_CONFIG.pauseOnHover && setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <div
                className={`hsp-track${isFade ? ' fade-mode' : ''}`}
                style={!isFade ? { transform: `translateX(-${currentIndex * 100}%)` } : undefined}
                aria-live="polite"
            >
                {banners.map((banner, idx) => {
                    const alignment = banner.textAlignment ?? 'LEFT';
                    const bgStyle   = banner.backgroundColor
                        ? { backgroundColor: banner.backgroundColor }
                        : {};

                    return (
                        <div
                            key={banner._id}
                            className={`hsp-slide${isFade && idx === currentIndex ? ' active' : ''}`}
                            style={bgStyle}
                            aria-hidden={idx !== currentIndex}
                        >
                            <picture>
                                {banner.mobileImageUrl && (
                                    <source
                                        media="(max-width: 575px)"
                                        srcSet={`${BASE_URL}${banner.mobileImageUrl}`}
                                    />
                                )}
                                {banner.tabletImageUrl && (
                                    <source
                                        media="(max-width: 991px)"
                                        srcSet={`${BASE_URL}${banner.tabletImageUrl}`}
                                    />
                                )}
                                <img
                                    src={`${BASE_URL}${banner.desktopImageUrl}`}
                                    alt={banner.headline}
                                    loading={idx === 0 ? 'eager' : 'lazy'}
                                />
                            </picture>

                            <div className={`hsp-overlay align-${alignment}`}>
                                <div className="hsp-content">
                                    <h2 className="hsp-headline">{banner.headline}</h2>
                                    {banner.subHeading && (
                                        <p className="hsp-subheading">{banner.subHeading}</p>
                                    )}
                                    {banner.description && (
                                        <p className="hsp-description">{banner.description}</p>
                                    )}
                                    {banner.ctaLabel && banner.ctaUrl && (
                                        <button
                                            className="hsp-cta"
                                            onClick={() => trackClick(banner)}
                                            aria-label={banner.ctaLabel}
                                        >
                                            {banner.ctaLabel}
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                <path d="M5 12h14M12 5l7 7-7 7"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Arrows */}
            {SLIDER_CONFIG.showArrows && total > 1 && (
                <>
                    <button className="hsp-arrow hsp-arrow-prev" onClick={goPrev} aria-label="Previous slide">&#8249;</button>
                    <button className="hsp-arrow hsp-arrow-next" onClick={goNext} aria-label="Next slide">&#8250;</button>
                </>
            )}

            {/* Indicators */}
            {SLIDER_CONFIG.showIndicators && total > 1 && (
                <div className="hsp-indicators" role="tablist" aria-label="Slide indicators">
                    {banners.map((_, idx) => (
                        <button
                            key={idx}
                            role="tab"
                            aria-selected={idx === currentIndex}
                            aria-label={`Go to slide ${idx + 1}`}
                            className={`hsp-dot${idx === currentIndex ? ' active' : ''}`}
                            onClick={() => goTo(idx)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/* ── Public entry: wraps with its own Context provider ── */
export const HeroSliderPublic: React.FC = () => (
    <HeroSliderPublicProvider>
        <SliderInner />
    </HeroSliderPublicProvider>
);
